import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CfInfoDialog from "@/components/pop-ui/cf-info-dialog";
import { toast } from "@/components/pop-ui/pop-toast";
import { authClient } from "@/lib/auth/auth-client";
import { handleAuthCall } from "@/lib/auth/auth-utils";
import { getAuthHeadersWaitSign } from "@/lib/auth/td-utils";
import { useGlobalConfig } from "@/providers/global-config-context";
import { useTurnstile } from "@/providers/turnstile-provider";
import { trackLogin } from "@/track/gtm";
import { CodeInput, type CodeInputRef } from "../components/code-input";
import { Button, Input } from "../components/form-items";
import { formatPhoneDisplay, formatPhoneParam, handleAuthApiError, type UserFormErrors } from "../utils";

// 手机登录组件
export function LoginFormPhone({
  phone,
  area,
  onFinish
}: {
  phone: string;
  area: string;
  onFinish?: () => void;
}) {
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<UserFormErrors>({
    code: ""
  });
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const codeInputRef = useRef<CodeInputRef>(null);
  const {
    t
  } = useTranslation();
  const {
    globalConfig
  } = useGlobalConfig();
  const {
    isSdkReady,
    sdkError
  } = useTurnstile();

  // 获取当前session以获取用户ID
  const {
    data: session
  } = authClient.useSession();

  // 发送手机验证码mutation
  const {
    mutate: sendPhoneCode,
    isPending: isSendingCode
  } = useMutation({
    mutationFn: async (cfTurnstileToken: string) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set 30-second timeout to reset button
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        toast.error(t("toast.error.requestTimeout", "Request timeout"));
      }, 30000);
      try {
        // 获取包含 TD 参数的请求头（等待 sign 准备就绪）
        const requestHeaders = await getAuthHeadersWaitSign("phone-login", {
          ...(cfTurnstileToken && {
            "x-captcha-response": cfTurnstileToken
          })
        });
        const result = await handleAuthCall(authClient.phoneNumber.sendOtp({
          phoneNumber: formatPhoneParam(phone, area),
          fetchOptions: {
            headers: requestHeaders
          }
        }));

        // Clear timeout on success
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return result;
      } catch (error) {
        // Clear timeout on error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        throw error;
      }
    },
    onSuccess: () => {
      setIsTurnstileLoading(false); // Clear loading state on success
      setIsTurnstileProcessStarted(false); // Reset process state on success
      setTimeout(() => {
        codeInputRef.current?.startCountdown();
      }, 500);
      toast.success(t("toast.success.verificationCodeSentToYourPhone"));
    },
    onError: (error_0: any) => {
      setIsTurnstileLoading(false); // Clear loading state on error
      setIsTurnstileProcessStarted(false); // Reset process state on error
      handleAuthApiError(error_0, t("toast.error.sendCodeFailedTryLater"));
    }
  });

  // 手机号+验证码登录mutation
  const {
    mutate: loginWithPhone,
    isPending: isLoggingInWithPhone
  } = useMutation({
    mutationFn: async (cfTurnstileToken_0: string) => {
      // 获取包含 TD 参数的请求头（等待 sign 准备就绪）
      const requestHeaders_0 = await getAuthHeadersWaitSign("phone-login", {
        ...(cfTurnstileToken_0 && {
          "x-captcha-response": cfTurnstileToken_0
        })
      });
      const isVerified = await handleAuthCall(authClient.phoneNumber.verify({
        phoneNumber: formatPhoneParam(phone, area),
        code,
        fetchOptions: {
          headers: requestHeaders_0
        }
      }));
      setIsTurnstileLoading(false); // Clear loading state on success
      // Don't reset process state on success - page will redirect anyway
      if (isVerified) {
        if (onFinish) {
          onFinish();
        }
        // 登录成功埋点 - 从session中获取用户ID
        trackLogin({
          pop_user_id: session?.user?.id,
          status: "success"
        });
        return {
          success: true
        };
      } else {
        throw new Error("验证码错误");
      }
    },
    onError: (error_1: any) => {
      setIsTurnstileLoading(false); // Clear loading state on error
      setIsTurnstileProcessStarted(false); // Reset process state on error
      handleAuthApiError(error_1, t("toast.error.loginFailedTryLater"));
      // 登录失败埋点
      trackLogin({
        pop_user_id: "",
        status: "failed"
      });
      // Ensure button state is reset after any error (including blacklist errors)
      setCaptchaOpen(false);
    }
  });

  // Dialog open state for captcha
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false);
  const [isTurnstileProcessStarted, setIsTurnstileProcessStarted] = useState(false);
  // Track whether captcha is for login or sending code
  const [captchaMode, setCaptchaMode] = useState<"login" | "sendCode">("login");

  // Handle Turnstile completion for login
  const handleTurnstileSuccessForLogin = (token: string) => {
    setCaptchaOpen(false);
    // Don't clear loading state here - let the API call handle it
    loginWithPhone(token);
  };

  // Handle Turnstile completion for sending code
  const handleTurnstileSuccessForSendCode = (token_0: string) => {
    setCaptchaOpen(false);
    // Don't clear loading state here - let the API call handle it
    sendPhoneCode(token_0);
  };

  // Handle turnstile dialog open - set loading state
  const handleTurnstileOpen = (mode: "login" | "sendCode") => {
    // Check SDK readiness before opening dialog
    if (sdkError) {
      // SDK failed to load, show error and don't open dialog
      toast.error(t("gateway.error.475"));
      return;
    }
    if (!isSdkReady) {
      // SDK is still loading, do nothing (user can try again)
      return;
    }
    setIsTurnstileLoading(true);
    setIsTurnstileProcessStarted(true);
    setCaptchaMode(mode);
    setCaptchaOpen(true);
  };

  // Handle turnstile error - reset loading state
  const handleTurnstileError = () => {
    setIsTurnstileLoading(false);
    setIsTurnstileProcessStarted(false);
    setCaptchaOpen(false);
    // Show error message as toast for dialog verification failure
    toast.error(t("gateway.error.476"));
  };

  // Unified handler that routes to the correct function based on mode
  const handleTurnstileSuccess = (token_1: string) => {
    if (captchaMode === "sendCode") {
      handleTurnstileSuccessForSendCode(token_1);
    } else {
      handleTurnstileSuccessForLogin(token_1);
    }
  };

  // Auto-send verification code on page load (only if Turnstile is disabled)
  useEffect(() => {
    const sendInitialCode = async () => {
      // If Turnstile is globally disabled, send code immediately
      if (!globalConfig?.PUBLIC_TURNSTILE_ENABLED) {
        sendPhoneCode("");
      }
    };

    // Delay sending to ensure component is fully mounted
    setTimeout(() => {
      sendInitialCode();
    }, 500);
  }, [globalConfig?.PUBLIC_TURNSTILE_ENABLED]);

  // 处理登录
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    // Prevent re-submission if turnstile process has already started
    if (isTurnstileProcessStarted) {
      return;
    }
    setErrors({
      code: ""
    });
    // 校验验证码
    if (!code) {
      setErrors(prev => ({
        ...prev,
        code: t("toast.error.enterVerificationCode")
      }));
      return;
    } else if (code.length !== 6) {
      setErrors(prev_0 => ({
        ...prev_0,
        code: t("toast.error.enter6DigitVerificationCode")
      }));
      return;
    }

    // If Turnstile is globally disabled, proceed directly with login
    if (!globalConfig?.PUBLIC_TURNSTILE_ENABLED) {
      loginWithPhone("");
      return;
    }

    // Set captcha mode to login and open dialog
    handleTurnstileOpen("login");
  };

  // 发送手机验证码
  const handleSendCode = async () => {
    // Prevent re-submission if turnstile process has already started
    if (isTurnstileProcessStarted) {
      return;
    }

    // Reset timeout state when starting new send code attempt
    setIsTimedOut(false);
    setErrors(prev_1 => ({
      ...prev_1,
      code: ""
    }));

    // If Turnstile is globally disabled, send code immediately
    if (!globalConfig?.PUBLIC_TURNSTILE_ENABLED) {
      sendPhoneCode("");
      return;
    }

    // Set captcha mode to send code and open dialog
    handleTurnstileOpen("sendCode");
  };
  const formattedPhone = formatPhoneDisplay(phone, area);
  const isLoading = isLoggingInWithPhone || isSendingCode;
  const isTurnstileEnabled = globalConfig?.PUBLIC_TURNSTILE_ENABLED;

  // Determine if button should be in loading state
  const isButtonLoading = isLoading && !isTimedOut || isTurnstileEnabled && isTurnstileLoading;
  return <form onSubmit={handleLogin} className="space-y-3">
			<div className="space-y-2">
				<Input id="account" type="text" value={formattedPhone} disabled={true} className="bg-gray-100" />
			</div>

			<CfInfoDialog open={captchaOpen} onOpenChange={setCaptchaOpen} containerId="cf-turnstile-container-login-dialog" onTurnstileSuccess={handleTurnstileSuccess} onTurnstileError={handleTurnstileError} />

			<CodeInput ref={codeInputRef} value={code} onChange={setCode} onResend={handleSendCode} error={errors.code} disabled={isLoading} autoStartCountdown={false} />

			<Button type="submit" className="w-full" size="lg" loading={isButtonLoading}>
				{isButtonLoading ? t("button.loginInProgress") : t("button.login")}
			</Button>
		</form>;
}